import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import api from "../lib/api";
import { toast } from "sonner";

export default function Checkout() {
    const { items, total, clear, deliveryType: ctxDeliveryType, setDeliveryType } = useCart();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const presetType = searchParams.get("type") || ctxDeliveryType;
    const [orderType, setOrderType] = useState(presetType === "location" ? "location" : "car");
    const [submitting, setSubmitting] = useState(false);
    const submittedRef = useRef(false);

    const [form, setForm] = useState({
        customer_name: "",
        phone: "",
        notes: "",
        car_type: "",
        car_color: "",
        plate_number: "",
        location_link: "",
        address: "",
    });

    useEffect(() => {
        if (submittedRef.current) return;
        if (items.length === 0) {
            navigate("/cart", { replace: true });
        }
    }, [items.length, navigate]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const isValid = () => {
        if (!form.customer_name.trim() || !form.phone.trim()) return false;
        if (orderType === "car") {
            return form.car_type.trim() && form.car_color.trim() && form.plate_number.trim();
        }
        return form.address.trim().length > 0;
    };

    const submit = async () => {
        if (!isValid()) {
            toast.error("يرجى تعبئة جميع الحقول المطلوبة");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                customer_name: form.customer_name.trim(),
                phone: form.phone.trim(),
                order_type: orderType,
                payment_method: "online",
                notes: form.notes.trim(),
                items: items.map((i) => ({
                    item_id: i.item_id,
                    name: i.name,
                    price: i.price,
                    quantity: i.quantity,
                    variant: i.variant,
                    notes: i.notes || "",
                })),
                car_info:
                    orderType === "car"
                        ? {
                              car_type: form.car_type.trim(),
                              car_color: form.car_color.trim(),
                              plate_number: form.plate_number.trim(),
                          }
                        : null,
                location_info:
                    orderType === "location"
                        ? {
                              location_link: form.location_link.trim(),
                              address: form.address.trim(),
                          }
                        : null,
            };
            const res = await api.post("/orders", payload);
            const orderNumber = res.data.order_number;
            submittedRef.current = true;
            navigate(`/pay/${orderNumber}`, { replace: true });
            clear();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "تعذر إرسال الطلب");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="checkout-page" className="max-w-md mx-auto md:max-w-2xl px-4 py-6">
            <h1 className="section-title text-3xl mb-6">إتمام الطلب</h1>

            {/* Order type toggle */}
            <div className="grid grid-cols-2 gap-2 mb-5">
                <button
                    onClick={() => { setOrderType("car"); setDeliveryType("car"); }}
                    data-testid="order-type-car"
                    className={`rounded-2xl p-4 font-extrabold border-2 transition ${
                        orderType === "car"
                            ? "border-brand bg-white text-brand"
                            : "border-[var(--border-subtle)] bg-white text-text-muted"
                    }`}
                >
                    <div className="text-2xl mb-1">
                        <i className="fa-solid fa-car" />
                    </div>
                    توصيل للسيارة
                </button>
                <button
                    onClick={() => { setOrderType("location"); setDeliveryType("location"); }}
                    data-testid="order-type-location"
                    className={`rounded-2xl p-4 font-extrabold border-2 transition ${
                        orderType === "location"
                            ? "border-brand bg-white text-brand"
                            : "border-[var(--border-subtle)] bg-white text-text-muted"
                    }`}
                >
                    <div className="text-2xl mb-1">
                        <i className="fa-solid fa-location-dot" />
                    </div>
                    توصيل للموقع
                </button>
            </div>

            <div className="surface-card p-4 space-y-3">
                <Field label="الاسم" testId="field-name">
                    <input
                        type="text"
                        value={form.customer_name}
                        onChange={(e) => update("customer_name", e.target.value)}
                        data-testid="input-name"
                        className="input-style"
                        placeholder="اسمك الكريم"
                    />
                </Field>
                <Field label="رقم الجوال" testId="field-phone">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        data-testid="input-phone"
                        className="input-style"
                        placeholder="05xxxxxxxx"
                    />
                </Field>

                {orderType === "car" && (
                    <>
                        <Field label="نوع السيارة">
                            <input
                                type="text"
                                value={form.car_type}
                                onChange={(e) => update("car_type", e.target.value)}
                                data-testid="input-car-type"
                                className="input-style"
                                placeholder="مثال: كامري 2022"
                            />
                        </Field>
                        <Field label="لون السيارة">
                            <input
                                type="text"
                                value={form.car_color}
                                onChange={(e) => update("car_color", e.target.value)}
                                data-testid="input-car-color"
                                className="input-style"
                                placeholder="مثال: أبيض"
                            />
                        </Field>
                        <Field label="رقم اللوحة">
                            <input
                                type="text"
                                value={form.plate_number}
                                onChange={(e) => update("plate_number", e.target.value)}
                                data-testid="input-plate-number"
                                className="input-style"
                                placeholder="مثال: أ ب ج 1234"
                            />
                        </Field>
                    </>
                )}

                {orderType === "location" && (
                    <>
                        <Field label="رابط الموقع (اختياري)">
                            <input
                                type="url"
                                value={form.location_link}
                                onChange={(e) => update("location_link", e.target.value)}
                                data-testid="input-location-link"
                                className="input-style"
                                placeholder="https://maps.google.com/..."
                            />
                        </Field>
                        <Field label="وصف العنوان">
                            <textarea
                                rows={3}
                                value={form.address}
                                onChange={(e) => update("address", e.target.value)}
                                data-testid="input-address"
                                className="input-style"
                                placeholder="الحي، الشارع، تفاصيل تساعد على الوصول"
                            />
                        </Field>
                    </>
                )}

                <Field label="ملاحظات إضافية (اختياري)">
                    <textarea
                        rows={2}
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        data-testid="input-notes"
                        className="input-style"
                        placeholder="أي ملاحظات على الطلب"
                    />
                </Field>

                <Field label="طريقة الدفع">
                    <div
                        data-testid="payment-online-info"
                        className="rounded-xl border-2 border-brand bg-white p-3 flex items-center gap-3"
                    >
                        <div className="flex items-center gap-2 text-2xl text-text-main">
                            <i className="fa-brands fa-apple-pay" />
                            <i className="fa-brands fa-cc-visa" />
                            <i className="fa-brands fa-cc-mastercard" />
                        </div>
                        <div className="text-sm">
                            <div className="font-extrabold text-brand">دفع إلكتروني آمن</div>
                            <div className="text-text-muted text-xs">Apple Pay • مدى • بطاقة ائتمانية</div>
                        </div>
                    </div>
                </Field>
            </div>

            <div className="surface-card p-4 mt-4">
                <div className="flex items-center justify-between">
                    <span className="text-text-muted">المجموع</span>
                    <span className="text-2xl font-black text-text-main" data-testid="checkout-total">
                        {total.toFixed(2)} ر.س
                    </span>
                </div>
                <button
                    onClick={submit}
                    disabled={submitting}
                    data-testid="submit-order"
                    className="btn-brand block w-full text-center mt-4 disabled:opacity-60"
                >
                    {submitting ? (
                        <>
                            <i className="fa-solid fa-spinner fa-spin ms-2" /> جاري الإرسال...
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-lock ms-2" /> متابعة للدفع
                        </>
                    )}
                </button>
            </div>

            <style>{`
                .input-style {
                    width: 100%;
                    padding: 0.75rem 1rem;
                    background: var(--bg);
                    border: 1.5px solid var(--border-subtle);
                    border-radius: 0.875rem;
                    font-family: inherit;
                    font-size: 0.95rem;
                    color: var(--text);
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .input-style:focus {
                    outline: none;
                    border-color: var(--brand);
                    box-shadow: 0 0 0 3px rgba(211,84,0,0.12);
                }
            `}</style>
        </div>
    );
}

function Field({ label, children, testId }) {
    return (
        <label className="block" data-testid={testId}>
            <span className="block text-sm font-bold text-text-main mb-1.5">{label}</span>
            {children}
        </label>
    );
}
